import {
	FontIconName,
	SvgIconValue
} from "client/Components";
import { resolveFormattedTextString } from "client/Misc/utils";
import { localize } from "./culture";

export type ActionGroup = {
	kind: "regular" | "alternative";
	name: string;
	title: string;
	icon?: FontIconName | SvgIconValue;
} | {
	kind: "virtual";
	name: string;
};

export const enum ActionKind {
	Regular = 0,
	MultiSelectOption = 1,
	Divider = 2,
	BusyIndicator = 3,
	SelectOption = 4
}

export type ActionTitle<TContext> = string | ((context: TContext) => string);
export type ActionIsChecked<TContext> = boolean | ((context: TContext) => boolean);

export type CommonActionProperties<TContext> = {
	name: string;
	title: ActionTitle<TContext>;
	icon?: FontIconName | SvgIconValue;
	canExecute(context: TContext): boolean;
	execute(context: TContext): void;
	align?: ActionBarItemAlignment;
};

export type RegularAction<TContext> = CommonActionProperties<TContext> & {
	kind: ActionKind.Regular;
	group?: ActionGroup;
	isDisabled?: (context: TContext) => boolean;
	closeMenuOnClick?: boolean;
};

export type MultiSelectOptionAction<TContext> = CommonActionProperties<TContext> & {
	kind: ActionKind.MultiSelectOption;
	group: ActionGroup;
	isChecked?: ActionIsChecked<TContext>;
	isDisabled?: boolean;
	closeMenuOnClick?: boolean;
};

export type SelectOptionAction<TContext> = CommonActionProperties<TContext> & {
	kind: ActionKind.SelectOption;
	group: ActionGroup;
	isChecked?: ActionIsChecked<TContext>;
	isDisabled?: boolean;
	closeMenuOnClick?: boolean;
};

export type DividerAction<TContext> = {
	kind: ActionKind.Divider;
	group?: ActionGroup;
	name: string;
	canExecute(context: TContext): boolean;
	align?: ActionBarItemAlignment;
	// The below properties represent the other CommonActionProperties which are never expected to be
	// provided, but defining them like this makes it easier to work with in typescript
	title?: ActionTitle<TContext>;
	icon?: FontIconName;
	execute?(context: TContext): void;
};

export type BusyIndicatorAction<TContext> = {
	kind: ActionKind.BusyIndicator;
	group: ActionGroup;
	name: string;
	canExecute(context: TContext): boolean;
	// The below properties represent the other CommonActionProperties which are never expected to be
	// provided, but defining them like this makes it easier to work with in typescript
	title?: ActionTitle<TContext>;
	icon?: FontIconName;
	execute?(context: TContext): void;
	align?: ActionBarItemAlignment;
};

export type Action<TContext> =
	| RegularAction<TContext>
	| MultiSelectOptionAction<TContext>
	| SelectOptionAction<TContext>;

export type OtherAction<TContext> =
	| DividerAction<TContext>
	| BusyIndicatorAction<TContext>;

export type ActionAndOthers<TContext> =
	| Action<TContext>
	| OtherAction<TContext>;

export type RadioButtonGroupState<TContext> = {
	checked: MultiSelectOptionAction<TContext> | undefined;
	unchecked: MultiSelectOptionAction<TContext>[];
};

export type ActionsWithContext<TContext, TAction extends ActionAndOthers<TContext> = ActionAndOthers<TContext>> = {
	actions: readonly TAction[];
	context: TContext;
};

export enum ActionBarItemAlignment {
	left = "left",
	right = "right"
}

export function reduceToAvailableActions<
	TAction extends { canExecute?: (context: TContext) => boolean; },
	TContext
>(
	source: readonly TAction[],
	context: TContext
): TAction[] {
	const availableActions: TAction[] = source.filter(
		(action) => {
			if (!action.canExecute) {
				return true;
			}

			return action.canExecute(context);
		}
	);

	return availableActions;
}

export function resolveActionTitle<TContext>(
	title: ActionTitle<TContext>,
	context: TContext
): string {
	if (typeof title === "string") {
		return title;
	}
	return title(context);
}

export function deselectAllTitle(selectionOrAmount: number | readonly unknown[]): string {
	const amount: number = typeof selectionOrAmount === "number"
		? selectionOrAmount
		: selectionOrAmount.length;

	return resolveFormattedTextString({
		template: localize("_DeselectAllWithNumber"),
		replacements: [
			{
				what: "{{Number}}",
				filler: amount.toString()
			}
		]
	});
}

export type ActionBuilderActions<TContext, T extends ActionAndOthers<TContext>> = {
	mainActions?: readonly T[];
	defaultActions?: readonly T[];
	contextualActions?: readonly T[];
	selectionActions?: readonly T[];
	selectionModeActions?: readonly T[];
};

export function buildActionsForTopBar<TContext, T extends ActionAndOthers<TContext>>(
	actions: ActionBuilderActions<TContext, T>,
	hasSelection: boolean,
	isResponsive: boolean,
	shouldShowSelectionModeActions: boolean
): readonly T[] {
	const {
		mainActions = [],
		defaultActions = [],
		contextualActions = [],
		selectionActions = [],
		selectionModeActions = []
	} = actions;

	const result: T[] = [];

	if (shouldShowSelectionModeActions) {
		result.push(...selectionModeActions);
	}

	if (hasSelection) {
		result.push(
			...selectionActions,
			...contextualActions
		);
	} else {
		if (!isResponsive) {
			result.push(...mainActions);
		}

		result.push(...defaultActions);
	}

	return result;
}
